import { useMemo } from "react";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { IGroupClassTimeSlot, IGroupClassDateAvailability } from "../models";
import { isBookableStatus } from "../../../../common/utilities/slotStatusUtility";

interface IBulkGroupClassResponse {
  [dateKey: string]: IGroupClassTimeSlot[];
}

/**
 * Hook to fetch bulk availability for group classes (14-day window)
 */
export const useBulkGroupClassAvailability = (params: {
  instructorId: number | undefined;
  className: string | undefined;
  startDate: Date | undefined;
  studioId: number | undefined;
}) => {
  const { instructorId, className, startDate, studioId } = params;

  const startDateString = startDate
    ? startDate.toISOString().split("T")[0]
    : undefined;

  const {
    data: bulkData,
    isLoading,
    error,
  } = useApiQuery<IBulkGroupClassResponse>({
    queryKey: [
      endpointsMetadata.groupClasses.getBulkAvailability,
      instructorId,
      className,
      startDateString,
      studioId,
    ],
    endpoint: `${endpointsMetadata.groupClasses.getBulkAvailability}?instructorId=${instructorId || ''}&className=${encodeURIComponent(className || '')}&startDate=${startDateString}&studioId=${studioId}`,
    enabled:
      instructorId !== undefined &&
      !!className &&
      !!startDateString &&
      !!studioId,
    globalIsLoading: false,
  });

  // Convert backend data to frontend format
  const dateAvailabilityMap = useMemo((): Map<string, IGroupClassDateAvailability> => {
    if (!bulkData || !startDate) return new Map();

    const availabilityMap = new Map<string, IGroupClassDateAvailability>();

    // Generate all 14 days from start date (matching appointment wizard behavior)
    for (let i = 0; i < 14; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(currentDate.getDate() + i);

      const dateKey = currentDate.toISOString().split("T")[0];
      const slots = bulkData[dateKey] || [];

      // Day is available if it has any bookable slots (Available or WaitlistAvailable)
      // that are not already booked by all family members
      const hasBookableSlots = slots.some(
        (slot) =>
          isBookableStatus(slot.status) &&
          !slot.allFamilyMembersHaveReservation
      );

      availabilityMap.set(dateKey, {
        date: new Date(dateKey + "T12:00:00"),
        hasAvailableSlots: hasBookableSlots,
        slots,
      });
    }

    return availabilityMap;
  }, [bulkData, startDate]);

  // Helper function to get availability for a specific date
  const getDateAvailability = (date: Date): IGroupClassDateAvailability | undefined => {
    const dateKey = date.toISOString().split("T")[0];
    return dateAvailabilityMap.get(dateKey);
  };

  // Helper function to check if a date has bookable slots (Available or WaitlistAvailable)
  const hasAvailableSlots = (date: Date): boolean => {
    const availability = getDateAvailability(date);
    return availability?.hasAvailableSlots || false;
  };

  // Helper function to check if a date has ANY slots at all (including fully booked ones)
  const hasAnySlots = (date: Date): boolean => {
    const availability = getDateAvailability(date);
    return (availability?.slots?.length ?? 0) > 0;
  };

  // Get the number of visible days
  const visibleDaysCount = dateAvailabilityMap.size;

  return {
    dateAvailabilityMap,
    getDateAvailability,
    hasAvailableSlots,
    hasAnySlots,
    isLoading,
    error,
    hasData: !!bulkData,
    visibleDaysCount,
  };
};
